/**
 * Conversational voice mode — explicit state machine (one reducer, not
 * scattered booleans). Pure, no React import, so it's testable on its own;
 * ChatInput.tsx drives it via useReducer(voiceReducer, initialVoiceState).
 *
 *   IDLE -> LISTENING -> CAPTURING -> TRANSCRIBING -> THINKING -> SPEAKING -> LISTENING (loop)
 *                                        ^________________________________|
 *                                     SPEAKING -> CAPTURING (barge-in, Segment V1.3;
 *                                     no-op when BARGE_IN_ENABLED is false — that flag
 *                                     gates whether BARGE_IN_DETECTED is ever dispatched,
 *                                     not anything in this reducer)
 *
 * Pressing the mic in ANY state goes straight to IDLE (MIC_PRESSED is
 * handled before the per-state switch) and cancels everything in flight —
 * callers key off `turnId` to ignore async work that resolves after the
 * user has already moved on (a stale STT/TTS response, a VAD callback from
 * a cancelled turn, etc).
 *
 * ABORT_TURN is the general error-recovery event: STT/TTS/chat-request
 * failures don't always know exactly which state they're recovering from
 * (e.g. a chat-stream error can fire after some chunks already reached
 * SPEAKING) — ABORT_TURN returns to LISTENING from any non-idle state,
 * so error handlers don't need to guess.
 */

export type VoiceState =
  | "IDLE"
  | "LISTENING"
  | "CAPTURING"
  | "TRANSCRIBING"
  | "THINKING"
  | "SPEAKING";

export interface VoiceMachineState {
  state: VoiceState;
  /** Bumped on every transition that starts or cancels a turn/session — use
   * to guard async callbacks against acting on a state that's since moved on. */
  turnId: number;
}

export type VoiceEvent =
  | { type: "MIC_PRESSED" }
  | { type: "SESSION_TIMEOUT" }
  | { type: "ABORT_TURN" }
  | { type: "SPEECH_DETECTED" }
  | { type: "TURN_ENDED" }
  | { type: "TURN_DISCARDED" }
  | { type: "TRANSCRIPT_READY" }
  | { type: "TRANSCRIPT_EMPTY" }
  | { type: "RESPONSE_READY" }
  | { type: "PLAYBACK_DONE" }
  | { type: "BARGE_IN_DETECTED" };

export const initialVoiceState: VoiceMachineState = { state: "IDLE", turnId: 0 };

export function voiceReducer(current: VoiceMachineState, event: VoiceEvent): VoiceMachineState {
  if (event.type === "MIC_PRESSED") {
    return current.state === "IDLE"
      ? { state: "LISTENING", turnId: current.turnId + 1 }
      : { state: "IDLE", turnId: current.turnId + 1 };
  }
  if (event.type === "SESSION_TIMEOUT") {
    return current.state === "IDLE" ? current : { state: "IDLE", turnId: current.turnId + 1 };
  }
  if (event.type === "ABORT_TURN") {
    return current.state === "IDLE" || current.state === "LISTENING"
      ? current
      : { ...current, state: "LISTENING" };
  }

  switch (current.state) {
    case "LISTENING":
      if (event.type === "SPEECH_DETECTED") return { ...current, state: "CAPTURING" };
      return current;

    case "CAPTURING":
      if (event.type === "TURN_ENDED") return { ...current, state: "TRANSCRIBING" };
      if (event.type === "TURN_DISCARDED") return { ...current, state: "LISTENING" };
      return current;

    case "TRANSCRIBING":
      if (event.type === "TRANSCRIPT_READY") return { ...current, state: "THINKING" };
      if (event.type === "TRANSCRIPT_EMPTY") return { ...current, state: "LISTENING" };
      return current;

    case "THINKING":
      if (event.type === "RESPONSE_READY") return { ...current, state: "SPEAKING" };
      return current;

    case "SPEAKING":
      if (event.type === "PLAYBACK_DONE") return { ...current, state: "LISTENING" };
      // turnId bumps here: barge-in supersedes the in-flight turn (its chat
      // response may still be streaming) with a brand new one, same as
      // MIC_PRESSED/SESSION_TIMEOUT — callers guard on turnId to stop the
      // superseded turn's callbacks from feeding the new turn's playback.
      if (event.type === "BARGE_IN_DETECTED") return { state: "CAPTURING", turnId: current.turnId + 1 };
      return current;

    case "IDLE":
    default:
      return current;
  }
}
